import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { X as XIcon, Plus, Trash2, Edit2, Loader2, AlertCircle } from "lucide-react";
import { api } from "../lib/api";
import { formatApiError } from "../lib/api";

const CATEGORIES = [
  ["CLEANING",      "Cleaning"],
  ["TRANSPORT",     "Transport"],
  ["REPOSITIONING", "Repositioning"],
  ["INSPECTION",    "Inspection"],
  ["REPAIR",        "Repair"],
  ["DAMAGE",        "Damage"],
  ["AGENT_FEE",     "Agent fee"],
  ["OTHER",         "Other"],
];
const CURRENCIES = ["USD", "EUR"];

/**
 * Off-hire cost tracker for a rental assignment.
 *
 * Props:
 *   assignment — rental assignment doc { id, tank_number, customer_code, pickup_date, drop_off_date }
 *   onClose    — () => void
 *   canEdit    — boolean, controls add/edit/delete
 */
export default function OffhireCostsDialog({ assignment, onClose, canEdit }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);  // null = closed, {} = new, { id } = edit
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/rentals/offhire-costs?assignment_id=${assignment.id}`);
      setItems(data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [assignment.id]);

  useEffect(() => { load(); }, [load]);

  const totals = items.reduce(
    (acc, it) => {
      if (it.invoice_status === "VOID") return acc;
      acc.cost += Number(it.cost_amount || 0);
      if (it.billable) acc.billable += Number(it.invoice_amount ?? it.cost_amount ?? 0);
      return acc;
    },
    { cost: 0, billable: 0 },
  );
  const margin = totals.billable - totals.cost;

  const removeLine = async (it) => {
    if (!window.confirm(`Delete ${it.category_label || it.category} line?`)) return;
    try {
      await api.delete(`/rentals/offhire-costs/${it.id}`);
      toast.success("Removed");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 overflow-y-auto"
      data-testid="offhire-costs-dialog"
    >
      <div className="bg-white border border-gray-300 max-w-4xl w-full shadow-2xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <h2 className="font-display text-xl font-bold">Off-hire costs</h2>
            <div className="text-xs text-gray-500 font-mono mt-0.5">
              {assignment.tank_number} · {assignment.customer_code} ·
              {" "}{assignment.pickup_date} → {assignment.drop_off_date || "ON-HIRE"}
            </div>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-800" data-testid="offhire-close">
            <XIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div className="grid grid-cols-3 gap-3 text-sm">
            <SummaryTile label="Cost total" value={totals.cost} tone="rose" testid="offhire-total-cost" />
            <SummaryTile label="Billable total" value={totals.billable} tone="sky" testid="offhire-total-billable" />
            <SummaryTile label="Margin impact" value={margin} tone={margin >= 0 ? "emerald" : "amber"} testid="offhire-margin" signed />
          </div>

          {loading ? (
            <div className="py-10 text-center text-gray-400 text-sm">
              <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading…
            </div>
          ) : items.length === 0 ? (
            <div className="py-8 text-center text-gray-400 text-sm" data-testid="offhire-empty">
              No off-hire costs logged yet.
              {canEdit && (
                <>
                  {" "}
                  <button
                    onClick={() => setEditing({})}
                    className="text-[#006E9E] font-semibold hover:underline"
                  >
                    Log the first one
                  </button>
                  .
                </>
              )}
            </div>
          ) : (
            <table className="ps-table" data-testid="offhire-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Category</th>
                  <th>Description</th>
                  <th>Vendor</th>
                  <th className="text-right">Cost</th>
                  <th className="text-right">Bill</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => (
                  <tr key={it.id} data-testid={`offhire-row-${it.id}`} className={it.invoice_status === "VOID" ? "opacity-40 line-through" : ""}>
                    <td className="font-mono text-xs">{it.incurred_at}</td>
                    <td className="font-semibold">{it.category_label || it.category}</td>
                    <td className="text-xs">{it.description}</td>
                    <td className="text-xs text-gray-600">{it.vendor || "—"}</td>
                    <td className="text-right font-mono">
                      {it.cost_currency} {Number(it.cost_amount || 0).toFixed(2)}
                    </td>
                    <td className="text-right font-mono">
                      {it.billable
                        ? `${it.invoice_currency || it.cost_currency} ${Number(it.invoice_amount ?? it.cost_amount ?? 0).toFixed(2)}`
                        : "—"}
                    </td>
                    <td>
                      <StatusChip status={it.invoice_status} />
                    </td>
                    <td className="text-right">
                      {canEdit && it.invoice_status === "PENDING" && (
                        <div className="flex justify-end gap-1">
                          <button
                            onClick={() => setEditing(it)}
                            className="text-gray-500 hover:text-[#006E9E]"
                            title="Edit"
                            data-testid={`offhire-edit-${it.id}`}
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => removeLine(it)}
                            className="text-gray-500 hover:text-red-600"
                            title="Delete"
                            data-testid={`offhire-delete-${it.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                      {canEdit && it.invoice_status === "NOT_BILLABLE" && (
                        <div className="flex justify-end gap-1">
                          <button
                            onClick={() => setEditing(it)}
                            className="text-gray-500 hover:text-[#006E9E]"
                            title="Edit"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => removeLine(it)}
                            className="text-gray-500 hover:text-red-600"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="border-t border-gray-200 px-6 py-4 flex justify-between items-center">
          <div className="text-[11px] text-gray-500 flex items-center gap-1.5">
            <AlertCircle className="w-3.5 h-3.5" />
            Billable PENDING lines auto-roll onto the customer's next monthly FDA invoice.
          </div>
          <div className="flex gap-2">
            {canEdit && (
              <button
                onClick={() => setEditing({})}
                className="ps-btn ps-btn-primary"
                data-testid="offhire-add"
              >
                <Plus className="w-4 h-4" /> Add cost
              </button>
            )}
            <button onClick={onClose} className="ps-btn ps-btn-secondary">Close</button>
          </div>
        </div>
      </div>

      {editing && (
        <OffhireEditDialog
          assignment={assignment}
          entry={editing.id ? editing : null}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
          saving={saving}
          setSaving={setSaving}
        />
      )}
    </div>
  );
}


function SummaryTile({ label, value, tone, testid, signed }) {
  const toneClass = {
    rose:    "bg-rose-50 text-rose-800 border-rose-200",
    sky:     "bg-sky-50 text-[#006E9E] border-sky-200",
    emerald: "bg-emerald-50 text-emerald-800 border-emerald-200",
    amber:   "bg-amber-50 text-amber-800 border-amber-300",
  }[tone] || "bg-gray-50 text-gray-700 border-gray-200";
  const fmt = (n) => {
    const s = Math.abs(n).toFixed(2);
    return signed && n < 0 ? `-$${s}` : `$${s}`;
  };
  return (
    <div className={`border ${toneClass} px-3 py-2 rounded-sm`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.16em] font-bold opacity-80">{label}</div>
      <div className="font-mono text-xl font-black mt-0.5">{fmt(value)}</div>
    </div>
  );
}

function StatusChip({ status }) {
  const map = {
    PENDING:       "bg-amber-100 text-amber-900 border-amber-300",
    INVOICED:      "bg-green-100 text-green-800 border-green-300",
    VOID:          "bg-gray-100 text-gray-500 border-gray-300",
    NOT_BILLABLE:  "bg-gray-100 text-gray-700 border-gray-300",
  };
  return (
    <span className={`inline-block px-2 py-0.5 text-[10px] uppercase tracking-wider font-bold rounded-sm border ${map[status] || map.PENDING}`}>
      {status === "NOT_BILLABLE" ? "Cost only" : status}
    </span>
  );
}


// ─── Add/Edit form ───────────────────────────────────────────────
function OffhireEditDialog({ assignment, entry, onClose, onSaved, saving, setSaving }) {
  const editMode = !!entry;
  const [form, setForm] = useState({
    category:         entry?.category || "CLEANING",
    description:      entry?.description || "",
    incurred_at:      entry?.incurred_at || new Date().toISOString().slice(0, 10),
    vendor:           entry?.vendor || "",
    cost_amount:      entry?.cost_amount ?? "",
    cost_currency:    entry?.cost_currency || "USD",
    billable:         entry?.billable ?? false,
    invoice_amount:   entry?.invoice_amount ?? "",
    invoice_currency: entry?.invoice_currency || entry?.cost_currency || "USD",
    notes:            entry?.notes || "",
  });

  const save = async () => {
    if (!form.description.trim()) return toast.error("Description required");
    if (!(Number(form.cost_amount) >= 0)) return toast.error("Cost amount required");
    setSaving(true);
    try {
      const body = {
        assignment_id: assignment.id,
        category: form.category,
        description: form.description.trim(),
        incurred_at: form.incurred_at,
        vendor: form.vendor.trim() || null,
        cost_amount: Number(form.cost_amount),
        cost_currency: form.cost_currency,
        billable: !!form.billable,
        invoice_amount: form.billable && form.invoice_amount !== ""
          ? Number(form.invoice_amount)
          : null,
        invoice_currency: form.billable ? form.invoice_currency : null,
        notes: form.notes.trim() || null,
      };
      if (editMode) {
        await api.patch(`/rentals/offhire-costs/${entry.id}`, body);
        toast.success("Updated");
      } else {
        await api.post(`/rentals/offhire-costs`, body);
        toast.success("Logged");
      }
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/60 flex items-start justify-center p-4 overflow-y-auto" data-testid="offhire-edit-dialog">
      <div className="bg-white border border-gray-300 max-w-lg w-full shadow-2xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="font-display text-lg font-bold">{editMode ? "Edit off-hire cost" : "Add off-hire cost"}</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-800">
            <XIcon className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Category *">
              <select
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                className="ps-input w-full"
                data-testid="offhire-category"
              >
                {CATEGORIES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </Field>
            <Field label="Date incurred *">
              <input
                type="date"
                value={form.incurred_at}
                onChange={(e) => setForm({ ...form, incurred_at: e.target.value })}
                className="ps-input w-full"
                data-testid="offhire-date"
              />
            </Field>
          </div>
          <Field label="Description *">
            <input
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="e.g. Interior wash at Antwerp depot"
              className="ps-input w-full"
              data-testid="offhire-desc"
            />
          </Field>
          <Field label="Vendor (optional)">
            <input
              value={form.vendor}
              onChange={(e) => setForm({ ...form, vendor: e.target.value })}
              placeholder="e.g. ANT-Wash BV"
              className="ps-input w-full"
            />
          </Field>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Cost paid *">
              <input
                type="number" step="0.01" min="0"
                value={form.cost_amount}
                onChange={(e) => setForm({ ...form, cost_amount: e.target.value })}
                className="ps-input w-full font-mono"
                data-testid="offhire-cost"
              />
            </Field>
            <Field label="Currency">
              <select
                value={form.cost_currency}
                onChange={(e) => setForm({ ...form, cost_currency: e.target.value, invoice_currency: form.invoice_currency || e.target.value })}
                className="ps-input w-full"
              >
                {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
            <div className="flex items-end">
              <label className="flex items-center gap-2 text-sm pb-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.billable}
                  onChange={(e) => setForm({ ...form, billable: e.target.checked })}
                  data-testid="offhire-billable"
                />
                Bill to customer
              </label>
            </div>
          </div>
          {form.billable && (
            <div className="grid grid-cols-2 gap-3 border border-sky-200 bg-sky-50 rounded-sm p-3">
              <Field label="Amount to invoice (overrides cost)">
                <input
                  type="number" step="0.01" min="0"
                  value={form.invoice_amount}
                  onChange={(e) => setForm({ ...form, invoice_amount: e.target.value })}
                  placeholder={`default: ${form.cost_amount || "cost"}`}
                  className="ps-input w-full font-mono"
                  data-testid="offhire-invoice-amount"
                />
              </Field>
              <Field label="Invoice currency">
                <select
                  value={form.invoice_currency}
                  onChange={(e) => setForm({ ...form, invoice_currency: e.target.value })}
                  className="ps-input w-full"
                >
                  {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </Field>
              <div className="col-span-2 text-[11px] text-sky-900">
                Will auto-attach to the next FDA invoice for {assignment.customer_code}.
              </div>
            </div>
          )}
          <Field label="Notes">
            <textarea
              rows={2}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className="ps-input w-full"
            />
          </Field>
        </div>
        <div className="border-t border-gray-200 px-6 py-4 flex justify-end gap-2">
          <button onClick={onClose} className="ps-btn ps-btn-secondary">Cancel</button>
          <button onClick={save} disabled={saving} className="ps-btn ps-btn-primary" data-testid="offhire-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (editMode ? "Save changes" : "Log cost")}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">
        {label}
      </label>
      {children}
    </div>
  );
}
